import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ApolloQueryResult } from '@apollo/client'
import { Row, Col, Form, Select, Button, message } from 'antd'
import DatePicker from 'components/Common/DayjsDatePicker'
import { ShopOutlined } from '@ant-design/icons'
import { SelectPrefixWrapper } from './../Widgets'
import { CalendarIcon, LocationIcon } from '@slerp/assets'
import { useSlerp } from '@slerp/client'
import { Dayjs } from 'dayjs'
import Loading from './../Utils/Loading'
import { isNull } from 'lodash'
import { Decimal } from 'decimal.js'
import TopLoyaltyCustomers from './TopLoyaltyCustomers'
import LoyaltyStats from './LoyaltyStats'
import { useMerchant, useLoyaltyOverview } from 'hooks/useGlobalContext'
import { applyLoyaltySourceFilter, LoyaltySource } from './utils'
import {
  StoreType,
  LoyaltyCustomer,
  HandleTableSort,
  LoyaltyCustomerListItem,
  LoyaltyCustomerExportItem
} from './types'

export type { LoyaltyCustomer, HandleTableSort }

const { RangePicker } = DatePicker
const dateFormat = 'DD/MM/YYYY'

type CSVCustomerData = {
  customer: string
  email: string
  current_stamps: number
  current_rewards: number
  rewards_redeemed_online: number
  rewards_redeemed_in_store: number
  all_time_rewards_redeemed_online: number
  all_time_rewards_redeemed_in_store: number
  lifetime_stamps_online: number
  lifetime_stamps_in_store: number
  total_spent: number
}

const EXPORT_PAGE_SIZE = 100
const EXPORT_CONCURRENCY = 5

// Runs `factories` with at most `limit` in flight at once, preserving
// result order — plain Promise.all(factories) would fire every page's
// request simultaneously, which doesn't scale to a large customer count.
const runWithConcurrencyLimit = async <T,>(
  factories: Array<() => Promise<T>>,
  limit: number
): Promise<T[]> => {
  const results: T[] = new Array(factories.length)
  let next = 0
  let stopped = false
  const workers = Array.from(
    { length: Math.min(limit, factories.length) },
    async () => {
      while (!stopped && next < factories.length) {
        const current = next++
        try {
          results[current] = await factories[current]()
        } catch (error) {
          // Stops other workers from starting more factories; already
          // in-flight ones still finish (no abort-signal plumbing for a
          // one-off export failure).
          stopped = true
          throw error
        }
      }
    }
  )
  await Promise.all(workers)
  return results
}

const LoyaltyOverview = () => {
  const { merchantLocations } = useMerchant()
  const { user } = useSlerp()
  const {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    storeIds,
    setStoreIds,
    optInFilter,
    setOptInFilter,
    activeSort,
    setActiveSort,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    resetPage,
    getOffset,
    customers,
    customersTotalCount,
    customersTotalCountLoading,
    loyaltyCustomersList,
    setLoyaltyCustomersList,
    paginatedCustomersList,
    setPaginatedCustomersList,
    setSelectedDropdownOption,
    loyaltySource,
    setLoyaltySource,
    hasLoadedOverviewData,
    setHasLoadedOverviewData,
    loadCustomersAggregate,
    loyaltyCustomersAggregateRefetch,
    loyaltyCustomersAggregateLoading,
    refetchLoyaltyCustomersList,
    loyaltyCustomersListLoading,
    loadLoyaltyCustomersPage,
    loyaltyCustomersPageLoading,
    loadLoyaltyCustomersExportPage
  } = useLoyaltyOverview()

  const [isExporting, setIsExporting] = useState(false)

  // antd fires onOpenChange(false) more than once per close (panel
  // dismissal + outside-click) — only a true open->close transition should
  // commit.
  const wasDateRangeOpenRef = useRef(false)

  // Uncommitted pick — LoyaltyOverviewLoader's queries treat
  // startDate/endDate as reactive Apollo variables, so only committing on
  // close (below) keeps them from firing on every click.
  const [pendingDateRange, setPendingDateRange] = useState<[Dayjs, Dayjs]>([
    startDate,
    endDate
  ])

  // hasLoadedOverviewData is already true on a later remount (e.g.
  // returning from another Loyalty tab), so this is then a no-op — no
  // refetch.
  useEffect(() => {
    if (hasLoadedOverviewData) return
    if (merchantLocations.length === 0) {
      // Zero stores is a resolved state, not a pending fetch — mark
      // loaded so the stats gate doesn't wait forever for a fetch that
      // never fires.
      setHasLoadedOverviewData(true)
      return
    }
    loadCustomersAggregate()
  }, [
    merchantLocations,
    hasLoadedOverviewData,
    loadCustomersAggregate,
    setHasLoadedOverviewData
  ])

  const isStatsReady =
    hasLoadedOverviewData &&
    !loyaltyCustomersAggregateLoading &&
    !loyaltyCustomersListLoading &&
    !customersTotalCountLoading

  const changeDates = (dates: Array<Dayjs>) => {
    const [changedStartDate, changedEndDate] = dates
    setPendingDateRange([changedStartDate, changedEndDate])
  }

  const handleStoreChange = (value: string) => {
    setSelectedDropdownOption(value)
    switch (value) {
      case 'all_stores': {
        const storeDataIds = merchantLocations.map(({ id }: StoreType) => id)

        setStoreIds(storeDataIds)
        break
      }

      case 'bulk_operation':
        setStoreIds([])
        break

      default:
        setStoreIds([value])
        break
    }
    setActiveSort('total-spent-desc')
    resetPage()
  }

  const handleTableSort = ({
    sortBy: newSortBy,
    sortDirection: newSortDirection,
    id
  }: HandleTableSort): Promise<ApolloQueryResult<any>> | undefined => {
    if (!refetchLoyaltyCustomersList) return
    setActiveSort(id)
    setSortBy(newSortBy)
    setSortDirection(newSortDirection)
    // Real pagination needs this: without resetting to page 1, a merchant
    // on page 2+ would keep viewing that offset against newly-sorted data.
    resetPage()
    return refetchLoyaltyCustomersList({
      sortBy: newSortBy,
      sortDirection: newSortDirection
    })
  }

  const handlePageChange = (page: number, newPageSize: number) => {
    // Set even when page 1 skips the fetch below — otherwise the picked
    // size reverts to the default the next time this tab remounts, since
    // it's the value AntD's own pagination control is controlled by.
    setPageSize(newPageSize)
    // loyaltySource has no server-side equivalent, so a per-page fetch here
    // would filter an unrelated raw batch — AntD paginates the full loaded
    // set locally instead (see tableCustomersList below).
    if (page !== 1 && !loyaltySource) {
      loadLoyaltyCustomersPage({
        variables: {
          merchantId: user?.merchant?.id ?? '',
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
          storeIds,
          customerIds: (customers || []).map(({ id }) => id),
          optInFilter,
          limit: newPageSize,
          offset: getOffset(page, newPageSize),
          sortBy,
          sortDirection
        }
      })
    }
  }

  const filteredLoyaltyCustomersList = useMemo(
    () => applyLoyaltySourceFilter(loyaltyCustomersList, loyaltySource),
    [loyaltyCustomersList, loyaltySource]
  )

  // Page 1 mirrors the stats source above; only page 2+ uses the per-page
  // fetch — except under a loyaltySource filter, which stays on the full
  // loaded set for every page (see handlePageChange above).
  const tableCustomersList = useMemo(
    () =>
      currentPage === 1 || loyaltySource
        ? filteredLoyaltyCustomersList
        : applyLoyaltySourceFilter(paginatedCustomersList, loyaltySource),
    [
      currentPage,
      filteredLoyaltyCustomersList,
      paginatedCustomersList,
      loyaltySource
    ]
  )

  const { stampsEarned, rewardsEarned, rewardsRedeemed, totalSpent } = useMemo(
    () =>
      filteredLoyaltyCustomersList.reduce(
        (acc, c) => ({
          stampsEarned:
            acc.stampsEarned +
            (c.periodStampsOnline ?? 0) +
            (c.periodStampsInStore ?? 0),
          rewardsEarned: acc.rewardsEarned + (c.currentRewards ?? 0),
          rewardsRedeemed:
            acc.rewardsRedeemed +
            (c.periodRewardsOnline ?? 0) +
            (c.periodRewardsInStore ?? 0),
          totalSpent: acc.totalSpent + (c.totalSpent ?? 0)
        }),
        { stampsEarned: 0, rewardsEarned: 0, rewardsRedeemed: 0, totalSpent: 0 }
      ),
    [filteredLoyaltyCustomersList]
  )

  const buildCSV = (list: LoyaltyCustomerExportItem[]) => {
    const items: CSVCustomerData[] = list.map((c) => ({
      customer: `${c.firstName} ${c.lastName}`,
      email: c.email,
      current_stamps: c.currentStamps ?? 0,
      current_rewards: c.currentRewards ?? 0,
      rewards_redeemed_online: c.periodRewardsOnline ?? 0,
      rewards_redeemed_in_store: c.periodRewardsInStore ?? 0,
      all_time_rewards_redeemed_online: c.lifetimeRewardsOnline ?? 0,
      all_time_rewards_redeemed_in_store: c.lifetimeRewardsInStore ?? 0,
      lifetime_stamps_online: c.lifetimeStampsOnline ?? 0,
      lifetime_stamps_in_store: c.lifetimeStampsInStore ?? 0,
      total_spent: c.totalSpent ?? 0
    }))
    if (items.length === 0) return ''
    const replacer = (_key: string, value: unknown) =>
      isNull(value) ? 0 : value
    const header = Object.keys(items[0]) as (keyof CSVCustomerData)[]
    const humanized_header = header.map((title) => title.replace(/_/g, ' '))
    return [
      humanized_header.join(','),
      ...items.map((row) =>
        header.map((field) => JSON.stringify(row[field], replacer)).join(',')
      )
    ].join('\r\n')
  }

  const downloadCSV = (csv: string) => {
    if (!csv) return
    const blob = new Blob(['﻿' + csv], {
      type: 'text/csv;charset=utf-8;'
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${
      user?.merchant?.slug
    }_customer_loyalty_stats_${Date.now()}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Fetched only on click — the full customersTotalCount, not just the
  // page-1 batch the table uses. EXPORT_PAGE_SIZE only chunks the network
  // requests; it's not a cap on how many rows get exported.
  const handleExport = async () => {
    setIsExporting(true)
    try {
      const rowsToFetch = customersTotalCount
      const pageCount = Math.ceil(rowsToFetch / EXPORT_PAGE_SIZE)
      const pageFactories = Array.from({ length: pageCount }, (_, page) => {
        const offset = page * EXPORT_PAGE_SIZE
        return () =>
          loadLoyaltyCustomersExportPage({
            variables: {
              merchantId: user?.merchant?.id ?? '',
              startDate: startDate.format('YYYY-MM-DD'),
              endDate: endDate.format('YYYY-MM-DD'),
              storeIds,
              customerIds: (customers || []).map(({ id }) => id),
              optInFilter,
              limit: Math.min(EXPORT_PAGE_SIZE, rowsToFetch - offset),
              offset,
              sortBy,
              sortDirection
            }
          })
      })
      const results = await runWithConcurrencyLimit(
        pageFactories,
        EXPORT_CONCURRENCY
      )
      const fullList: LoyaltyCustomerExportItem[] = results.reduce(
        (
          acc: LoyaltyCustomerExportItem[],
          result: {
            data?: { loyaltyCustomersList?: LoyaltyCustomerExportItem[] }
          }
        ) => acc.concat(result.data?.loyaltyCustomersList ?? []),
        []
      )
      downloadCSV(buildCSV(applyLoyaltySourceFilter(fullList, loyaltySource)))
    } catch (_error) {
      message.error('Unable to export customers. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <Row justify='space-between' className='_mt-16 _mb-0'>
        <Row gutter={8} className='_mb-0'>
          <Col className='orders-form-item _mb-0'>
            <SelectPrefixWrapper prefixicon={<LocationIcon />}>
              <Select
                placeholder='Filter by Location'
                onChange={handleStoreChange}
                defaultValue='all_stores'
                allowClear={false}
                className='control'
                style={{
                  minWidth: 'max-content'
                }}
                dropdownStyle={{
                  minWidth: '200px'
                }}
              >
                <Select.Option value={'all_stores'} key={'all_stores'}>
                  All locations
                </Select.Option>
                <Select.Option value={'bulk_operation'} key={'bulk_operation'}>
                  Bulk Operation
                </Select.Option>
                {merchantLocations.map((store: StoreType) => (
                  <Select.Option value={store.id} key={store.id}>
                    {store.name}
                  </Select.Option>
                ))}
              </Select>
            </SelectPrefixWrapper>
          </Col>
          <Col>
            <SelectPrefixWrapper prefixicon={<ShopOutlined />}>
              <Select
                placeholder='Loyalty source'
                onChange={(value: string) => {
                  setLoyaltySource(
                    value === 'all' ? undefined : (value as LoyaltySource)
                  )
                  // Resets the page — otherwise a merchant on page 2+ keeps
                  // viewing that offset against the newly-filtered data,
                  // same as every other filter here (store, sort, date).
                  resetPage()
                }}
                defaultValue='all'
                allowClear={false}
                className='control'
                style={{ minWidth: 'max-content' }}
                dropdownStyle={{ minWidth: '220px' }}
              >
                <Select.Option value='all'>All sources</Select.Option>
                <Select.Option value='in_store'>
                  In store (offline)
                </Select.Option>
                <Select.Option value='online'>Online</Select.Option>
              </Select>
            </SelectPrefixWrapper>
          </Col>
          <Col>
            <Form.Item name='date' className='_mb-0'>
              <SelectPrefixWrapper prefixicon={<CalendarIcon />}>
                <RangePicker
                  format={dateFormat}
                  allowClear={false}
                  onChange={(e: any) => {
                    changeDates(e)
                  }}
                  onOpenChange={(open) => {
                    if (open) {
                      wasDateRangeOpenRef.current = true
                      return
                    }
                    if (!wasDateRangeOpenRef.current) return
                    wasDateRangeOpenRef.current = false

                    const [pendingStartDate, pendingEndDate] = pendingDateRange
                    const datesChanged =
                      !startDate.isSame(pendingStartDate, 'day') ||
                      !endDate.isSame(pendingEndDate, 'day')
                    if (!datesChanged) return

                    // Committing here triggers the fetch via
                    // LoyaltyOverviewLoader's reactive variables — no
                    // manual refetch call needed.
                    setStartDate(pendingStartDate)
                    setEndDate(pendingEndDate)
                    setActiveSort('total-spent-desc')
                    resetPage()
                  }}
                  placeholder={['Start date', 'End date']}
                  defaultValue={[startDate, endDate]}
                  className='_pl-48'
                />
              </SelectPrefixWrapper>
            </Form.Item>
          </Col>
        </Row>
        <Col>
          <Button
            loading={isExporting}
            // customersTotalCount/customers lag one request behind a
            // filter change — block export until they catch up so it
            // can't fire against a stale count or an unrelated customer set.
            disabled={
              loyaltyCustomersAggregateLoading ||
              loyaltyCustomersListLoading ||
              customersTotalCountLoading
            }
            onClick={handleExport}
            data-testid='customer-csv-export'
          >
            Export Customers
          </Button>
        </Col>
      </Row>
      {isStatsReady ? (
        <LoyaltyStats
          loyaltyCustomers={
            loyaltySource
              ? filteredLoyaltyCustomersList.length
              : customersTotalCount
          }
          stampsEarned={stampsEarned}
          rewardsEarned={rewardsEarned}
          rewardsRedeemed={rewardsRedeemed}
          totalSpent={totalSpent}
          averageBasketSize={
            new Decimal(
              customers?.reduce((acc: number, cust: LoyaltyCustomer) => {
                if (cust?.orders?.aggregate?.sum?.total) {
                  return cust.orders.aggregate.sum.total + acc
                }
                return acc
              }, 0)
            )
              .dividedBy(
                new Decimal(
                  customers?.reduce((acc: number, cust: LoyaltyCustomer) => {
                    if (cust?.orders?.aggregate?.count) {
                      return cust.orders.aggregate.count + acc
                    }
                    return acc
                  }, 0)
                )
              )
              .toNumber() || 0
          }
        />
      ) : (
        <Loading />
      )}
      <TopLoyaltyCustomers
        loading={loyaltyCustomersListLoading || loyaltyCustomersPageLoading}
        data={{ loyaltyCustomersList: tableCustomersList }}
        // Clamped to the loaded set under a loyaltySource filter — see
        // handlePageChange above for why real pagination is skipped then.
        total={
          loyaltySource
            ? filteredLoyaltyCustomersList.length
            : customersTotalCount
        }
        customers={customers}
        toggleOptIn={() => {
          setOptInFilter(!optInFilter)
          resetPage()
        }}
        optInFilter={optInFilter}
        customerAggregateRefetch={loyaltyCustomersAggregateRefetch}
        activeSort={activeSort}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        handleTableSort={handleTableSort}
        onStampSuccess={(customerId, stampsAdded) => {
          const applyStamp = (list: LoyaltyCustomerListItem[]) =>
            list.map((c) =>
              c.id === customerId
                ? { ...c, currentStamps: (c.currentStamps ?? 0) + stampsAdded }
                : c
            )
          setLoyaltyCustomersList(applyStamp(loyaltyCustomersList))
          setPaginatedCustomersList(applyStamp(paginatedCustomersList))
        }}
      />
    </>
  )
}

export default LoyaltyOverview
